import { useCallback } from 'react';
import { useChatPanelOptional } from '../context/chatPanelContext';
import { useChat } from './useChat';

/**
 * Dashboard-friendly helper: open chat panel and jump to a specific conversation.
 * This is intentionally best-effort (no throw) so dashboards can call it safely.
 */
export function useOpenChatConversation() {
    const panel = useChatPanelOptional();
    const { loadConversations, selectConversation } = useChat();

    const openByMatcher = useCallback(
        async (matcher) => {
            if (!panel) return { ok: false, conversation: null };
            panel.openPanel();

            const loaded = await loadConversations();
            const list = Array.isArray(loaded) ? loaded : [];
            const hit = list.find(matcher);
            if (hit?.id) {
                selectConversation(hit.id);
                return { ok: true, conversation: hit };
            }
            return { ok: false, conversation: null };
        },
        [panel, loadConversations, selectConversation],
    );

    const openGroupByName = useCallback(
        async (name) => {
            const out = await openByMatcher(
                (c) => c?.kind === 'group' && String(c?.name ?? '').trim() === String(name ?? '').trim(),
            );
            return out.ok;
        },
        [openByMatcher],
    );

    return { openGroupByName, openByMatcher, hasChatPanel: Boolean(panel) };
}

